import { useEffect, useRef, useCallback } from 'react';

export function useWebSocket(onMessage, onOpen) {
  const wsRef        = useRef(null);
  const handlersRef  = useRef(onMessage);
  const onOpenRef    = useRef(onOpen);
  const reconnectRef = useRef(null);
  const mountedRef   = useRef(true);
  const queueRef     = useRef([]); // messages queued before socket opens

  useEffect(() => { handlersRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onOpenRef.current   = onOpen;    }, [onOpen]);

  const send = useCallback((type, payload = {}) => {
    const msg = JSON.stringify({ type, ...payload });
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    } else {
      // Queue — will flush on open
      queueRef.current.push(msg);
    }
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      // Flush queued messages
      queueRef.current.forEach(m => ws.send(m));
      queueRef.current = [];
      // Notify consumer socket is ready
      if (onOpenRef.current) onOpenRef.current();
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (handlersRef.current) handlersRef.current(msg);
      } catch {}
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      reconnectRef.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { send };
}
